import axios from 'axios';

export type LimitKind = 'stake' | 'loss';
export type LimitPeriod = 'day' | 'week' | 'month';

export interface BettingLimit {
  kind: LimitKind;
  period: LimitPeriod;
  amount: number;
  used: number;
  /** A raise waiting out the protection period. */
  pending_amount?: number;
  /** Removal requested; the limit stays until pending_from. */
  pending_remove: boolean;
  pending_from?: string;
}

export interface LimitsResponse {
  limits: BettingLimit[];
  protection_days: number;
}

export interface AccountLockState {
  locked: boolean;
  permanent?: boolean;
  locked_until?: string;
  reason?: string;
}

export type LockDuration = '7d' | '14d' | '30d' | 'permanent';

const base = () => process.env.REACT_APP_GAME_API_BACKEND_API ?? '';
const noCache = { headers: { 'Cache-Control': 'no-cache' } };

export async function getLimits(): Promise<LimitsResponse> {
  const { data } = await axios.get(`${base()}/player/limits`, noCache);
  return data;
}

export async function setLimit(
  kind: LimitKind,
  period: LimitPeriod,
  amount: number
): Promise<LimitsResponse> {
  const { data } = await axios.post(`${base()}/player/limits`, {
    kind,
    period,
    amount: amount.toFixed(2),
  });
  return data;
}

export async function removeLimit(
  kind: LimitKind,
  period: LimitPeriod
): Promise<LimitsResponse> {
  const { data } = await axios.post(`${base()}/player/limits/remove`, {
    kind,
    period,
  });
  return data;
}

export async function getAccountLock(): Promise<AccountLockState> {
  const { data } = await axios.get(`${base()}/player/account_lock`, noCache);
  return data;
}

export async function lockAccount(
  duration: LockDuration,
  reason: string
): Promise<AccountLockState> {
  const { data } = await axios.post(`${base()}/player/account_lock`, {
    duration,
    reason,
  });
  return data;
}

export interface PlayerProfile {
  title: string;
  first_name: string;
  last_name: string;
  email: string;
  date_of_birth: string;
  street: string;
  house_number: string;
  zip_code: string;
  city: string;
  country: string;
  phone: string;
}

export interface PlayerBonus {
  id: number;
  title: string;
  amount: string;
  created_at: string;
}

export async function getProfile(): Promise<PlayerProfile> {
  const { data } = await axios.get(`${base()}/player/profile`, noCache);
  return data;
}

export async function saveProfile(p: PlayerProfile): Promise<PlayerProfile> {
  const { data } = await axios.post(`${base()}/player/profile`, p);
  return data;
}

export type ChangeEmailResult =
  | { ok: true; profile: PlayerProfile }
  | {
      ok: false;
      code: 'wrong_password' | 'invalid_email' | 'email_taken' | 'error';
    };

export async function changeEmail(
  email: string,
  password: string
): Promise<ChangeEmailResult> {
  try {
    const { data } = await axios.post(`${base()}/player/email`, {
      email,
      password,
    });
    return { ok: true, profile: data };
  } catch (e: any) {
    const code = e?.response?.data?.code;
    return {
      ok: false,
      code:
        code === 'wrong_password' ||
        code === 'invalid_email' ||
        code === 'email_taken'
          ? code
          : 'error',
    };
  }
}

export async function getBonuses(): Promise<PlayerBonus[]> {
  const { data } = await axios.get(`${base()}/player/bonuses`, noCache);
  return data?.bonuses ?? [];
}
